import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin, Clock, Ruler, Mountain, Compass, CalendarCheck, Satellite, Layers } from 'lucide-react';

// Centro indicativo dell'altopiano della Sila
const SILA = { center: [16.55, 39.25], zoom: 9.2, pitch: 58, bearing: -22 };
const TERRAIN_TILES = ['https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png'];
const SAT_TILES = ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'];
const LABEL_TILES = ['https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'];

// Perimetro APPROSSIMATIVO del Parco Nazionale della Sila (da raffinare con GeoJSON ufficiale)
const PARK = {
  type: 'Feature',
  properties: { name: 'Parco Nazionale della Sila' },
  geometry: {
    type: 'Polygon',
    coordinates: [[
      [16.415, 39.420], [16.510, 39.445], [16.610, 39.430], [16.710, 39.375],
      [16.775, 39.300], [16.780, 39.215], [16.730, 39.120], [16.640, 39.055],
      [16.520, 39.030], [16.415, 39.070], [16.350, 39.160], [16.345, 39.270],
      [16.375, 39.360], [16.415, 39.420],
    ]],
  },
};
const PARK_CENTER = [16.55, 39.235];
const PARK_COLOR = '#39d98a';

// Sequenza per l'effetto "flusso" del tratteggio lungo il percorso
const DASH_SEQ = [
  [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5],
  [3, 4, 0], [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2],
  [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
];

const buildStyle = () => ({
  version: 8,
  glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  sources: {
    sat: { type: 'raster', tiles: SAT_TILES, tileSize: 256, maxzoom: 19, attribution: 'Imagery © Esri, Maxar, Earthstar Geographics' },
    labels: { type: 'raster', tiles: LABEL_TILES, tileSize: 256, maxzoom: 19 },
    dem: { type: 'raster-dem', tiles: TERRAIN_TILES, encoding: 'terrarium', tileSize: 256, maxzoom: 14 },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#0b1c14' } },
    { id: 'sat', type: 'raster', source: 'sat', paint: { 'raster-saturation': 0.2, 'raster-contrast': 0.18, 'raster-resampling': 'linear' } },
    { id: 'hillshade', type: 'hillshade', source: 'dem', paint: { 'hillshade-exaggeration': 0.5, 'hillshade-shadow-color': '#04180b', 'hillshade-highlight-color': '#f7ffe6' } },
    { id: 'labels', type: 'raster', source: 'labels', paint: { 'raster-opacity': 0.5 } },
  ],
  terrain: { source: 'dem', exaggeration: 1.6 },
  sky: {
    'sky-color': '#8fd0ff', 'sky-horizon-blend': 0.7,
    'horizon-color': '#eaf7ff', 'horizon-fog-blend': 0.6,
    'fog-color': '#dceee0', 'fog-ground-blend': 0.5,
    'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 12, 0.4],
  },
});

const lineFeature = (area) => {
  if (area.geojson && area.geojson.type) {
    const g = area.geojson.type === 'Feature' ? area.geojson.geometry : area.geojson;
    if (g && (g.type === 'LineString' || g.type === 'MultiLineString')) {
      return { type: 'Feature', properties: { id: area.id }, geometry: g };
    }
  }
  const pts = (area.points || []).filter((p) => p.lng != null && p.lat != null);
  if (pts.length >= 2) {
    return { type: 'Feature', properties: { id: area.id }, geometry: { type: 'LineString', coordinates: pts.map((p) => [Number(p.lng), Number(p.lat)]) } };
  }
  return null;
};

const areaBounds = (area) => {
  const b = new maplibregl.LngLatBounds();
  let has = false;
  const feat = lineFeature(area);
  if (feat) {
    const flat = feat.geometry.type === 'MultiLineString' ? feat.geometry.coordinates.flat() : feat.geometry.coordinates;
    flat.forEach((c) => { b.extend(c); has = true; });
  }
  (area.points || []).forEach((p) => { if (p.lng != null && p.lat != null) { b.extend([Number(p.lng), Number(p.lat)]); has = true; } });
  return has ? b : null;
};

const parkBounds = () => {
  const b = new maplibregl.LngLatBounds();
  PARK.geometry.coordinates[0].forEach((c) => b.extend(c));
  return b;
};

export const Sila3DExplorer = ({ areas = [], onBook, focusId = null, showPanel = true, showToggle = true }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const rafRef = useRef(null);
  const markersRef = useRef([]);
  const drawnRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState('satellite'); // 'satellite' | 'hybrid'
  const [failed, setFailed] = useState(false);

  // ---- Init mappa (una sola volta) ----
  useEffect(() => {
    if (mapRef.current || !ref.current) return;
    let map;
    try {
      map = new maplibregl.Map({
        container: ref.current,
        style: buildStyle(),
        center: SILA.center, zoom: SILA.zoom, pitch: SILA.pitch, bearing: SILA.bearing,
        maxPitch: 80, antialias: true, attributionControl: true,
      });
    } catch (err) {
      // WebGL non supportato / GPU non disponibile: mostra fallback invece di pagina vuota
      setFailed(true);
      return undefined;
    }
    mapRef.current = map;
    map.on('error', () => { /* non far crashare l'app per errori di tile */ });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true, showUserHeading: true }), 'top-right');
    map.addControl(new maplibregl.FullscreenControl(), 'top-right');
    if (!showPanel) { try { map.scrollZoom.disable(); } catch (e) { /* noop */ } }

    map.on('load', () => {
      try { map.setTerrain({ source: 'dem', exaggeration: 1.6 }); } catch (e) { /* noop */ }
      // Perimetro del Parco (protagonista)
      try {
        if (!map.getSource('park')) {
          map.addSource('park', { type: 'geojson', data: PARK });
          map.addLayer({ id: 'park-fill', type: 'fill', source: 'park', paint: { 'fill-color': PARK_COLOR, 'fill-opacity': 0.12 } });
          map.addLayer({ id: 'park-glow', type: 'line', source: 'park', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': PARK_COLOR, 'line-width': 12, 'line-blur': 10, 'line-opacity': 0.55 } });
          map.addLayer({ id: 'park-outline', type: 'line', source: 'park', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#eafff2', 'line-width': 2.5, 'line-opacity': 0.9, 'line-dasharray': [2, 2] } });
        }
        const el = document.createElement('div');
        el.className = 'sila-park-label';
        el.textContent = 'Parco Nazionale della Sila';
        markersRef.current.push(new maplibregl.Marker({ element: el }).setLngLat(PARK_CENTER).addTo(map));
      } catch (e) { /* noop */ }
      setReady(true);
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      drawnRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Disegna i percorsi quando mappa e dati sono pronti ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !areas.length || drawnRef.current) return;
    drawnRef.current = true;

    const dashLayers = [];

    areas.forEach((area) => {
      const srcId = `route-${area.id}`;
      const feat = lineFeature(area);
      const color = area.color || PARK_COLOR;

      if (feat && !map.getSource(srcId)) {
        map.addSource(srcId, { type: 'geojson', data: feat });
        map.addLayer({ id: `${srcId}-glow`, type: 'line', source: srcId, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': color, 'line-width': 16, 'line-blur': 14, 'line-opacity': 0.5 } });
        map.addLayer({ id: `${srcId}-line`, type: 'line', source: srcId, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': color, 'line-width': 4.5, 'line-opacity': 0.95 } });
        map.addLayer({ id: `${srcId}-dash`, type: 'line', source: srcId, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 2.4, 'line-opacity': 0.95, 'line-dasharray': [0, 4, 3] } });
        dashLayers.push(`${srcId}-dash`);

        map.on('mouseenter', `${srcId}-line`, () => setActive(area.id));
        map.on('mouseleave', `${srcId}-line`, () => setActive(null));
        map.on('click', `${srcId}-line`, () => onBook && onBook(area));
      }

      (area.points || []).forEach((p, i) => {
        if (p.lng == null || p.lat == null) return;
        const el = document.createElement('div');
        el.className = 'sila-poi';
        el.innerHTML = `<span class="sila-poi-pulse" style="--c:${color}"></span><span class="sila-poi-dot" style="--c:${color}">${i + 1}</span>`;
        const html = `<div class="sila-pop">${p.photo ? `<img src="${p.photo}" alt="${p.name || ''}"/>` : ''}<div class="sila-pop-body"><strong>${p.name || 'Punto panoramico'}</strong><p>${p.description || ''}</p><button data-book="${area.id}">Prenota questo tour</button></div></div>`;
        const popup = new maplibregl.Popup({ offset: 22, closeButton: true, maxWidth: '260px' }).setHTML(html);
        const marker = new maplibregl.Marker({ element: el }).setLngLat([Number(p.lng), Number(p.lat)]).setPopup(popup).addTo(map);
        popup.on('open', () => {
          const btn = document.querySelector(`button[data-book="${area.id}"]`);
          if (btn) btn.onclick = () => { popup.remove(); onBook && onBook(area); };
        });
        markersRef.current.push(marker);
      });
    });

    let step = 0;
    const animate = (ts) => {
      const ns = parseInt((ts / 55) % DASH_SEQ.length, 10);
      if (ns !== step) {
        dashLayers.forEach((id) => { if (map.getLayer(id)) map.setPaintProperty(id, 'line-dasharray', DASH_SEQ[ns]); });
        step = ns;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    // Intro cinematografica: inquadra tutto il PARCO
    try {
      const cam = map.cameraForBounds(parkBounds(), { padding: { top: 90, bottom: 90, left: 380, right: 90 } });
      if (cam) map.easeTo({ center: cam.center, zoom: Math.min(cam.zoom, 11.2), pitch: 60, bearing: -22, duration: 2800 });
    } catch (e) { /* noop */ }
  }, [areas, ready, onBook]);

  // ---- Evidenzia percorso attivo ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const boost = mode === 'hybrid' ? 1.6 : 1;
    areas.forEach((area) => {
      const glow = `route-${area.id}-glow`;
      const line = `route-${area.id}-line`;
      const dash = `route-${area.id}-dash`;
      if (!map.getLayer(glow)) return;
      const on = active === area.id;
      map.setPaintProperty(glow, 'line-width', (on ? 26 : 16) * (mode === 'hybrid' ? 1.2 : 1));
      map.setPaintProperty(glow, 'line-opacity', on ? 0.85 : 0.5);
      map.setPaintProperty(line, 'line-width', (on ? 6 : 4.5) * boost);
      if (map.getLayer(dash)) map.setPaintProperty(dash, 'line-width', 2.4 * boost);
    });
    if (map.getCanvas()) map.getCanvas().style.cursor = active ? 'pointer' : '';
  }, [active, areas, ready, mode]);

  // ---- Toggle Satellite / Ibrido ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const hybrid = mode === 'hybrid';
    try {
      if (map.getLayer('labels')) map.setPaintProperty('labels', 'raster-opacity', hybrid ? 1 : 0.5);
      if (map.getLayer('sat')) {
        map.setPaintProperty('sat', 'raster-saturation', hybrid ? -0.15 : 0.2);
        map.setPaintProperty('sat', 'raster-contrast', hybrid ? -0.05 : 0.18);
        map.setPaintProperty('sat', 'raster-brightness-max', hybrid ? 0.92 : 1);
      }
      if (map.getLayer('park-fill')) map.setPaintProperty('park-fill', 'fill-opacity', hybrid ? 0.2 : 0.12);
      if (map.getLayer('hillshade')) map.setPaintProperty('hillshade', 'hillshade-exaggeration', hybrid ? 0.75 : 0.5);
    } catch (e) { /* noop */ }
  }, [mode, ready]);

  const flyToRoute = useCallback((area) => {
    const map = mapRef.current;
    const b = areaBounds(area);
    if (!map || !b) return;
    const pad = showPanel ? { top: 90, bottom: 90, left: 380, right: 90 } : { top: 120, bottom: 120, left: 90, right: 90 };
    try {
      const cam = map.cameraForBounds(b, { padding: pad });
      if (cam) map.easeTo({ center: cam.center, zoom: Math.min(cam.zoom, 13.5), pitch: 64, bearing: -18, duration: 1800 });
    } catch (e) { /* noop */ }
  }, [showPanel]);

  // ---- Focus pilotato dall'esterno (scrollytelling) ----
  useEffect(() => {
    if (!ready || !focusId) return;
    const area = areas.find((a) => a.id === focusId);
    if (!area) return;
    setActive(focusId);
    flyToRoute(area);
  }, [focusId, ready, areas, flyToRoute]);

  const resetView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      const cam = map.cameraForBounds(parkBounds(), { padding: { top: 90, bottom: 90, left: 380, right: 90 } });
      if (cam) map.easeTo({ center: cam.center, zoom: Math.min(cam.zoom, 11.2), pitch: 60, bearing: -22, duration: 1600 });
    } catch (e) { /* noop */ }
  }, []);

  return (
    <div className="sila-explorer relative overflow-hidden rounded-[24px] border border-border shadow-[var(--shadow-soft)]">
      <div ref={ref} className="h-[74vh] min-h-[520px] w-full" data-testid="sila-3d-map" />

      {failed && (
        <div className="absolute inset-0 z-[6] flex items-center justify-center bg-muted/60 p-6 text-center backdrop-blur" data-testid="map-fallback">
          <div className="max-w-sm rounded-2xl border border-border bg-background/90 p-6 shadow-lg">
            <Mountain className="mx-auto mb-3 h-8 w-8 text-primary" />
            <p className="font-display text-lg font-semibold text-foreground">Mappa 3D non disponibile</p>
            <p className="mt-2 text-sm text-muted-foreground">Il tuo browser non supporta la grafica 3D (WebGL). Prova ad aggiornare il browser o ad attivare l'accelerazione hardware. Puoi comunque vedere e prenotare gli itinerari qui sotto.</p>
          </div>
        </div>
      )}

      {/* Toggle Satellite / Ibrido */}
      {showToggle && (
      <div className="absolute left-1/2 top-4 z-[5] flex -translate-x-1/2 items-center gap-1 rounded-full bg-background/85 p-1 shadow backdrop-blur" data-testid="map-mode-toggle">
        <button type="button" onClick={() => setMode('satellite')} data-testid="mode-satellite" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mode === 'satellite' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}>
          <Satellite className="h-3.5 w-3.5" /> Satellite
        </button>
        <button type="button" onClick={() => setMode('hybrid')} data-testid="mode-hybrid" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mode === 'hybrid' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}>
          <Layers className="h-3.5 w-3.5" /> Ibrido
        </button>
      </div>
      )}

      {/* Hint controlli */}
      <div className="pointer-events-none absolute right-4 bottom-4 z-[5] flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground shadow backdrop-blur">
        <Compass className="h-3.5 w-3.5 text-primary" /> Trascina per ruotare · Ctrl+trascina per inclinare
      </div>

      {/* Pannello itinerari */}
      {showPanel && areas.length > 0 && (
        <div className="absolute left-4 top-4 z-[5] w-[340px] max-w-[calc(100%-2rem)]" data-testid="route-panel">
          <button type="button" onClick={resetView} data-testid="reset-view" className="mb-2 flex w-full items-center justify-between rounded-xl bg-background/85 px-3 py-2 text-sm font-semibold text-foreground shadow backdrop-blur transition-colors hover:bg-background">
            <span className="inline-flex items-center gap-2"><Mountain className="h-4 w-4 text-primary" />Parco Nazionale della Sila</span>
            <span className="text-xs font-normal text-muted-foreground">{areas.length} itinerari</span>
          </button>
          <div className="flex max-h-[58vh] flex-col gap-2 overflow-y-auto pr-1">
            {areas.map((area) => (
              <button
                key={area.id}
                type="button"
                data-testid={`route-card-${area.id}`}
                onMouseEnter={() => setActive(area.id)}
                onMouseLeave={() => setActive(null)}
                onClick={() => flyToRoute(area)}
                className={`group flex items-stretch gap-3 overflow-hidden rounded-2xl border bg-background/90 p-2 text-left shadow-sm backdrop-blur transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md ${active === area.id ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`}
              >
                <span className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">
                  {area.cover_image ? (
                    <img src={area.cover_image} alt={area.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" />
                  ) : null}
                  <span className="absolute inset-x-0 bottom-0 h-1.5" style={{ background: area.color || PARK_COLOR }} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col justify-center">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: area.color || PARK_COLOR }} />
                    <span className="truncate font-display text-sm font-semibold text-foreground">{area.name}</span>
                  </span>
                  {area.guide?.display_name ? (
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      {area.guide.photo ? <img src={area.guide.photo} alt="" className="h-4 w-4 rounded-full object-cover" /> : null}
                      <span className="truncate">Guida: {area.guide.display_name}</span>
                    </span>
                  ) : null}
                  <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    {area.subtitle ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{area.subtitle}</span> : null}
                    {area.duration ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{area.duration}</span> : null}
                    {area.length_km ? <span className="inline-flex items-center gap-1"><Ruler className="h-3 w-3" />{area.length_km} km</span> : null}
                    {area.difficulty ? <span className="inline-flex items-center gap-1"><Mountain className="h-3 w-3" />{area.difficulty}</span> : null}
                  </span>
                  <span className="mt-1.5 flex items-center justify-between">
                    {area.price != null ? <span className="text-xs font-semibold text-foreground">€{area.price}<span className="font-normal text-muted-foreground">/persona</span></span> : <span />}
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`route-book-${area.id}`}
                      onClick={(e) => { e.stopPropagation(); onBook && onBook(area); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onBook && onBook(area); } }}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      <CalendarCheck className="h-3 w-3" /> Prenota
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sila3DExplorer;
